import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Send, Loader2, MessageSquare, Building2, User } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useSeo from "../../hooks/useSeo";
import { useSettingsStore } from "../../store/settingsStore";

const SUBJECTS = [
    { value: "general", label: "Genel" },
    { value: "advertising", label: "Reklam" },
    { value: "press", label: "Basın" },
    { value: "partnership", label: "İş Birliği" },
    { value: "other", label: "Diğer" },
];

export default function Contact() {
    const { t } = useTranslation();
    const { settings } = useSettingsStore();
    const [form, setForm] = useState({
        name: "",
        company: "",
        position: "",
        email: "",
        phone: "",
        subject: "general",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    useSeo({
        title: "İletişim",
        description: "Otel Pro ile iletişime geçin. Genel bilgi, reklam, basın ve iş birliği talepleri için.",
        path: "/iletisim",
    });

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            toast.success("Mesajınız iletildi. En kısa sürede dönüş yapılacaktır.");
            setForm({ name: "", company: "", position: "", email: "", phone: "", subject: "general", message: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const mapsUrl =
        settings?.google_maps_embed_url ||
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24091.57!2d28.9784!3d41.0082!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14caa7040068086b%3A0xe1ccfe98bc01b0b0!2sIstanbul!5e0!3m2!1sen!2str!4v1700000000";

    return (
        <div className="bg-light-bg min-h-[60vh]" data-testid="contact-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">{t("nav.contact")}</span>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">İletişim</h1>
                    <p className="mt-4 text-white/75 max-w-2xl">
                        Genel bilgi, reklam, basın ve iş birliği talepleri için bize ulaşın.
                    </p>
                </div>
            </section>

            <section className="py-14">
                <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
                    {/* Form */}
                    <div className="lg:col-span-2 bg-white rounded-lg border border-slate-100 shadow-md p-6">
                        <h2 className="text-xl font-bold text-dark-navy">Bize Yazın</h2>
                        <p className="mt-1 text-sm text-deep-blue/80">Genelde 1 iş günü içinde geri dönüş yapılır.</p>
                        <form className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={submit} data-testid="contact-form">
                            <Field label="Ad Soyad *" Icon={User}>
                                <input
                                    required
                                    minLength={2}
                                    value={form.name}
                                    onChange={(e) => update("name", e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-teal text-sm outline-none"
                                    data-testid="contact-name"
                                />
                            </Field>
                            <Field label="Firma" Icon={Building2}>
                                <input
                                    value={form.company}
                                    onChange={(e) => update("company", e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-teal text-sm outline-none"
                                    data-testid="contact-company"
                                />
                            </Field>
                            <Field label="Pozisyon">
                                <input
                                    value={form.position}
                                    onChange={(e) => update("position", e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-teal text-sm outline-none"
                                />
                            </Field>
                            <Field label="E-posta *">
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-teal text-sm outline-none"
                                    data-testid="contact-email"
                                />
                            </Field>
                            <Field label="Telefon">
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => update("phone", e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-teal text-sm outline-none"
                                />
                            </Field>
                            <Field label="Konu *">
                                <select
                                    value={form.subject}
                                    onChange={(e) => update("subject", e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-teal text-sm outline-none bg-white"
                                    data-testid="contact-subject"
                                >
                                    {SUBJECTS.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <div className="md:col-span-2">
                                <Field label="Mesaj *">
                                    <textarea
                                        required
                                        minLength={5}
                                        rows={5}
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-teal text-sm outline-none"
                                        data-testid="contact-message"
                                    />
                                </Field>
                            </div>
                            <div className="md:col-span-2 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="inline-flex items-center gap-2 bg-dark-navy hover:bg-deep-blue text-white font-semibold text-sm px-5 py-3 rounded-md disabled:opacity-70"
                                    data-testid="contact-submit"
                                >
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    Gönder
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Info */}
                    <aside className="space-y-4">
                        <div className="bg-dark-navy text-white rounded-lg p-6">
                            <h3 className="text-sm uppercase tracking-wider text-teal font-semibold">İletişim Bilgileri</h3>
                            <ul className="mt-4 space-y-3 text-sm text-white/85">
                                <li className="flex items-start gap-3">
                                    <Phone className="w-4 h-4 mt-0.5 text-teal" />
                                    <a href={`tel:${(settings.phone || "").replace(/\s/g, "")}`} className="hover:text-teal" data-testid="contact-phone">
                                        {settings.phone}
                                    </a>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Mail className="w-4 h-4 mt-0.5 text-teal" />
                                    <a href={`mailto:${settings.email}`} className="hover:text-teal" data-testid="contact-info-email">
                                        {settings.email}
                                    </a>
                                </li>
                                {settings.address && (
                                    <li className="flex items-start gap-3">
                                        <MapPin className="w-4 h-4 mt-0.5 text-teal" />
                                        <span>{settings.address}</span>
                                    </li>
                                )}
                            </ul>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-6">
                            <h3 className="text-sm uppercase tracking-wider text-deep-blue font-semibold inline-flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" /> Basın İletişimi
                            </h3>
                            <p className="mt-3 text-sm text-deep-blue/80 leading-relaxed">
                                Basın açıklamaları, röportaj ve veri paylaşımı talepleri için "Basın" konusu seçilerek gönderim yapılabilir.
                            </p>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-100 shadow-md overflow-hidden">
                            <iframe
                                title="Otel Pro Konum"
                                src={mapsUrl}
                                className="w-full h-64 border-0"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="contact-map"
                            />
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}

function Field({ label, Icon, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}
